import { BuildingOffice2Icon, PlusCircleIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback, useMemo } from 'react';
import { ActorRefFrom, SnapshotFrom } from 'xstate';
import { EmptyState, FormDialog, Pill } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import '~/components/pageHeaders.css';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { useEmployerFacilitiesRef } from '~/features/market/marketSelectors';
import { EmployerFacility } from '~/types/hlx/employerFacility';
import { employerFacilitiesMachine } from './employerFacilities.machine';
import { EmployerFacilityForm } from './EmployerFacilityForm';
import { formatEmployerFacilityLocation, getEmployerFacilityDisplayName } from './utils';

const TAGS = {
  addBtn: 'employer-facilities-add-button',
  emptyState: 'employer-facilities-empty-state',
  grid: 'employer-facilities-grid',
  layout: 'employer-facilities-layout',
};

type EmployerFacilitiesSnapshot = SnapshotFrom<typeof employerFacilitiesMachine>;
const selectEmployerFacilities = (state: EmployerFacilitiesSnapshot) =>
  state.context.employerFacilities;
const selectLoading = (state: EmployerFacilitiesSnapshot) => state.context.loading;
const selectFacilityRef = (state: EmployerFacilitiesSnapshot) => state.context.facilityRef;
const selectEmployerFacilityForm = (state: EmployerFacilitiesSnapshot) =>
  state.context.employerFacilityForm;
const selectModal = (state: EmployerFacilitiesSnapshot) => state.context.modal;

const EmployerFacilitiesNoRowsOverlay = () => (
  <EmptyState
    icon={<BuildingOffice2Icon className="size-10 text-grey-700" />}
    title="No facilities yet"
    cyTag={TAGS.emptyState}
  />
);

const createEmployerFacilityColumns = ({
  handleDelete,
  handleEdit,
}: {
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}): ColDef<EmployerFacility>[] => [
  { field: 'id', hide: true },
  {
    headerName: 'Facility',
    cellRenderer: ({ data }: CustomCellRendererProps<EmployerFacility>) => {
      if (!data) return null;
      const location = formatEmployerFacilityLocation(data);
      return (
        <div className="w-full text-left leading-tight">
          <div>{getEmployerFacilityDisplayName(data)}</div>
          <div>{location || 'Location unavailable'}</div>
        </div>
      );
    },
    valueGetter: ({ data }) => (data ? getEmployerFacilityDisplayName(data) : ''),
    filterValueGetter: ({ data }) => {
      if (!data) return '';
      return [
        getEmployerFacilityDisplayName(data),
        data.facilityNpi,
        formatEmployerFacilityLocation(data),
      ]
        .filter(Boolean)
        .join(' ');
    },
  },
  {
    headerName: 'NPI',
    field: 'facilityNpi',
    valueGetter: ({ data }) => data?.facilityNpi || '',
  },
  {
    headerName: 'Clinical Setting',
    valueGetter: ({ data }) =>
      [data?.clinicalSettingCategory, data?.clinicalSetting].filter(Boolean).join(' - '),
  },
  {
    headerName: 'Size',
    valueGetter: ({ data }) => [data?.facilitySize, data?.areaType].filter(Boolean).join(' - '),
  },
  {
    headerName: 'Primary',
    cellRenderer: ({ data }: CustomCellRendererProps<EmployerFacility>) =>
      data?.isPrimary ? (
        <Pill label="Primary" customStyle="bg-white text-black ring-grey-300/20" />
      ) : null,
    valueGetter: ({ data }) => (data?.isPrimary ? 'Primary' : ''),
    maxWidth: 130,
  },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: CT.certs.table.editBtn },
        { type: 'delete', handler: handleDelete, cyTag: CT.agGrid.deleteBtn },
      ] satisfies ActionDef[],
    },
  } as ColDef<EmployerFacility>,
];

export const EmployerFacilitiesPage = () => {
  const { employerFacilitiesRef } = useEmployerFacilitiesRef();

  if (!employerFacilitiesRef) {
    return null;
  }

  return (
    <section className="mobile-page-card w-full mx-auto rounded-xl border border-grey-900 bg-white p-5 text-left shadow-sm sm:p-8">
      <header className="mobile-page-header mb-5 border-b border-grey-900 pb-5 sm:mb-6 sm:pb-6">
        <h2 className="sub-heading-sb mb-2 text-grey-100">Facilities</h2>
        <p className="body-text text-grey-200">Manage your organization’s facilities.</p>
      </header>
      <EmployerFacilitiesPageInner employerFacilitiesRef={employerFacilitiesRef} />
    </section>
  );
};

const EmployerFacilitiesPageInner = ({
  employerFacilitiesRef,
}: {
  employerFacilitiesRef: ActorRefFrom<typeof employerFacilitiesMachine>;
}) => {
  const employerFacilities = useSelector(employerFacilitiesRef, selectEmployerFacilities);
  const loading = useSelector(employerFacilitiesRef, selectLoading);
  const facilityRef = useSelector(employerFacilitiesRef, selectFacilityRef);
  const employerFacilityForm = useSelector(employerFacilitiesRef, selectEmployerFacilityForm);
  const { create, edit } = useSelector(employerFacilitiesRef, selectModal);

  const handleOpen = useCallback(
    () => employerFacilitiesRef.send({ type: 'ON_ADD' }),
    [employerFacilitiesRef]
  );
  const handleClose = useCallback(
    () => employerFacilitiesRef.send({ type: 'ON_CLOSE_MODAL' }),
    [employerFacilitiesRef]
  );
  const handleEdit = useCallback(
    (id: number) => employerFacilitiesRef.send({ type: 'ON_EDIT', payload: { id } }),
    [employerFacilitiesRef]
  );
  const handleDelete = useCallback(
    (id: number) =>
      employerFacilitiesRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } }),
    [employerFacilitiesRef]
  );

  const colDefs = useMemo(
    () => createEmployerFacilityColumns({ handleDelete, handleEdit }),
    [handleDelete, handleEdit]
  );

  return (
    <div>
      <div className="mb-5 flex justify-end" data-cy={TAGS.layout}>
        <div>
          <button
            type="button"
            data-cy={TAGS.addBtn}
            className="flex items-center gap-2 rounded-lg px-3.5 py-2 body-text-sb text-primary-50 border border-primary-t-700 hover:bg-primary-t-900 hover:border-primary-t-600 transition-colors cursor-pointer"
            onClick={handleOpen}
          >
            <PlusCircleIcon className="size-5" aria-hidden="true" />
            Add Facility
          </button>
        </div>
      </div>
      <BasicGrid
        data={employerFacilities}
        loading={loading}
        colDefs={colDefs}
        pagination
        cyTag={TAGS.grid}
        noRowsOverlayComponent={EmployerFacilitiesNoRowsOverlay}
      />
      <FormDialog
        title={create ? 'Add Facility' : 'Edit Facility'}
        editing={edit}
        creating={create}
        onClose={handleClose}
      >
        <EmployerFacilityForm
          defaultValues={employerFacilityForm}
          facilityRef={facilityRef}
          onSubmitSuccess={(data) =>
            employerFacilitiesRef.send({
              type: create ? 'ON_SUBMIT' : 'ON_UPDATE',
              payload: { data },
            })
          }
          submitLabel={edit ? 'Save Changes' : 'Submit'}
        />
      </FormDialog>
    </div>
  );
};
